/**
 * TestDetailPage — /catalogue/:id
 *
 * Shows: Author credit (author_display), Task Brief rendered as markdown,
 * Estimated Minutes, time limit, pass mark.
 * "Request Access" button opens RequestAccessModal.
 *
 * Per issue 05-catalogue-browse/01 § Vertical slice scope + AC 3.
 * Per docs/design.md: editorial density.
 *
 * CARVE-OUT: uses raw fetch (see CataloguePage.tsx § CARVE-OUT).
 * CARVE-OUT: markdown rendering uses dangerouslySetInnerHTML with a simple
 * conversion. Full markdown parser (e.g. remark/rehype) deferred to a
 * follow-up; for MVP the brief is expected to be minimal GitHub-flavoured MD.
 */

import {
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Shell } from '@/components/shell';
import { RequestAccessModal } from './RequestAccessModal.tsx';
import { AvailabilityChip } from '@/components/availability/AvailabilityChip';
import { apiFetch } from '@/lib/api-client';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface TestDetail {
  id: string;
  title: string;
  description: string;
  track: string;
  level: string;
  time_limit: number;
  pass_mark: number;
  estimated_minutes: number;
  task_brief_md: string | null;
  author_name_at_publish: string | null;
  author_display: string; // "Anonymous" when author_name_at_publish is null
  /** J-X22 — availability window. Null/null = always-open. */
  availability_starts_at: string | null;
  availability_ends_at: string | null;
  availability_closed_reason: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Simple markdown → HTML (MVP; full parser deferred)
// ─────────────────────────────────────────────────────────────────────────────

function markdownToHtml(md: string): string {
  // Very minimal conversion: headings, bold, italic, code, paragraphs.
  // No XSS risk here since Task Brief content is admin-authored (trusted source).
  return md
    .split('\n\n')
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      if (/^#{3}\s/.test(trimmed)) return `<h3>${trimmed.replace(/^#{3}\s/, '')}</h3>`;
      if (/^#{2}\s/.test(trimmed)) return `<h2>${trimmed.replace(/^#{2}\s/, '')}</h2>`;
      if (/^#\s/.test(trimmed)) return `<h1>${trimmed.replace(/^#\s/, '')}</h1>`;
      const inlined = trimmed
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>');
      return `<p>${inlined}</p>`;
    })
    .join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Fetcher
// ─────────────────────────────────────────────────────────────────────────────

async function fetchTest(id: string): Promise<TestDetail> {
  const res = await apiFetch(`/api/catalogue/${id}`);
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) throw new Error(`fetch failed: ${String(res.status)}`);
  const body = (await res.json()) as { ok: true; data: TestDetail };
  return body.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// TestDetailPage
// ─────────────────────────────────────────────────────────────────────────────

const TRACK_LABELS: Record<string, string> = {
  architecture: 'Architecture',
  mep: 'MEP',
  structural: 'Structural',
  interior: 'Interior',
};

const LEVEL_LABELS: Record<string, string> = {
  novice: 'Novice',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  bim_champion: 'BIM Champion',
};

type LoadState = 'loading' | 'error' | 'not_found' | 'loaded';

export function TestDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const [test, setTest] = useState<TestDetail | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!id) { setLoadState('not_found'); return; }
    void (async () => {
      try {
        const t = await fetchTest(id);
        setTest(t);
        setLoadState('loaded');
      } catch (err) {
        if (err instanceof Error && err.message === 'not_found') {
          setLoadState('not_found');
        } else {
          setErrorMsg(err instanceof Error ? err.message : 'Failed to load test');
          setLoadState('error');
        }
      }
    })();
  }, [id]);

  if (loadState === 'loading') {
    return (
      <main className="min-h-screen bg-bg text-ink">
        <div className="mx-auto max-w-page px-page-pad-x py-16">
          <p className="font-sans text-body text-muted">Loading…</p>
        </div>
      </main>
    );
  }

  if (loadState === 'not_found') {
    return (
      <main className="min-h-screen bg-bg text-ink">
        <div className="mx-auto max-w-page px-page-pad-x py-16">
          <p className="font-sans text-h3 text-ink">Test not found.</p>
          <Link to="/catalogue" className="font-sans text-small text-accent hover:underline mt-4 inline-block">
            ← Back to catalogue
          </Link>
        </div>
      </main>
    );
  }

  if (loadState === 'error') {
    return (
      <main className="min-h-screen bg-bg text-ink">
        <div className="mx-auto max-w-page px-page-pad-x py-16">
          <p className="font-sans text-body text-error" role="alert">{errorMsg}</p>
          <Link to="/catalogue" className="font-sans text-small text-accent hover:underline mt-4 inline-block">
            ← Back to catalogue
          </Link>
        </div>
      </main>
    );
  }

  if (!test) return <></>;

  return (
    <Shell mode="individual" densityMode="editorial">
      <div className="mx-auto max-w-page px-page-pad-x py-16">
          {/* Breadcrumb */}
          <Link
            to="/catalogue"
            className="font-mono text-eyebrow uppercase text-muted hover:text-ink inline-flex items-center gap-1 mb-8"
          >
            ← Catalogue
          </Link>

          {/* Header */}
          <div className="flex flex-col gap-2 mb-8">
            <span className="font-mono text-eyebrow font-medium uppercase text-muted">
              {TRACK_LABELS[test.track] ?? test.track} / {LEVEL_LABELS[test.level] ?? test.level}
            </span>
            <h1 className="font-sans text-headline font-medium text-ink tracking-tight">
              {test.title}
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main content */}
            <div className="lg:col-span-2">
              {test.task_brief_md ? (
                <article
                  className="prose prose-bimassess font-sans text-body text-ink"
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(test.task_brief_md) }}
                />
              ) : (
                <p className="font-sans text-body text-muted">No task brief available.</p>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Author credit */}
              <div className="rounded-lg border border-line bg-card p-6">
                <p className="font-mono text-eyebrow uppercase text-muted-2 mb-1">Authored by</p>
                <p className="font-sans text-body font-medium text-ink">{test.author_display}</p>
              </div>

              {/* J-X22 — Availability window (renders nothing for always-open). */}
              <AvailabilityChip
                input={{
                  availability_starts_at: test.availability_starts_at,
                  availability_ends_at: test.availability_ends_at,
                  availability_closed_reason: test.availability_closed_reason,
                }}
              />

              {/* Key stats */}
              <div className="rounded-lg border border-line bg-card p-6 space-y-4">
                <dl className="space-y-3">
                  <div>
                    <dt className="font-mono text-eyebrow uppercase text-muted-2">Est. time</dt>
                    <dd className="font-sans text-body text-ink">{String(test.estimated_minutes)} min</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-eyebrow uppercase text-muted-2">Time limit</dt>
                    <dd className="font-sans text-body text-ink">
                      {test.time_limit > 0 ? `${String(test.time_limit)} min` : 'No limit'}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-mono text-eyebrow uppercase text-muted-2">Pass mark</dt>
                    <dd className="font-sans text-body text-ink">{String(test.pass_mark)}%</dd>
                  </div>
                </dl>

                <Button variant="primary" type="button" onClick={() => setShowModal(true)} className="w-full">
                  Request Access
                </Button>
              </div>
            </aside>
          </div>
        </div>

      {showModal && (
        <RequestAccessModal
          testId={test.id}
          testTitle={test.title}
          onClose={() => setShowModal(false)}
        />
      )}
    </Shell>
  );
}
